import BrButton from '@govbr/react-components/lib/BrButton';
import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { AcaoIndicativoNascimento } from '../../../api/indicativoNascimentoAPI';
import {
  limparIndicativoNascimento,
  registrarAcaoIndicativoNascimento,
  selectEtapaPerguntaAtual,
  selectFamiliaIndicativoNascimento,
  selectIntegranteIndicativoNascimento,
  selectRespostasIndicativo,
  setEtapaIndicativo,
  setEtapaPerguntaAtual,
  setRespostaIndicativo,
} from '../indicativoNascimentoSlice';
import { selectPerfilCompleto } from '../../Perfil/perfilSlice';
import { PERGUNTAS } from './perguntas';
import ModalNaoIncluir from '../ModalNaoIncluir/ModalNaoIncluir';
import styles from './IndicativoNascimentoPerguntas.module.css';

const IndicativoNascimentoPerguntas: React.FC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();

  const etapaAtual = useAppSelector(selectEtapaPerguntaAtual);
  const respostas = useAppSelector(selectRespostasIndicativo);
  const integrante = useAppSelector(selectIntegranteIndicativoNascimento);
  const familia = useAppSelector(selectFamiliaIndicativoNascimento);
  const perfil = useAppSelector(selectPerfilCompleto);

  const nomeCrianca = (integrante?.nome?.split(' ')?.[0] ?? '').toUpperCase();
  const nomeRF = (perfil?.nome?.split(' ')?.[0] ?? '').toUpperCase();

  const perguntasVisiveis = useMemo(() => {
    return PERGUNTAS.filter((p) => {
      if (p.id === 'sexo') return !integrante?.sexo;
      return p.exibir !== false;
    });
  }, [integrante?.sexo]);

  const pergunta = perguntasVisiveis[etapaAtual];
  const total = perguntasVisiveis.length;
  const isUltima = false;
  const respostaAtual = respostas[pergunta.id];

  const [modalAberto, setModalAberto] = useState(false);
  const [loadingNaoIncluir, setLoadingNaoIncluir] = useState(false);
  const [valoresInput, setValoresInput] = useState<Record<string, string>>(() => {
    const filiacaoSalva = respostas['filiacao'];
    if (filiacaoSalva) {
      try {
        const parsed = JSON.parse(filiacaoSalva);
        return { filiacao2: parsed.filiacao2 === 'nao_sei' ? '' : (parsed.filiacao2 ?? integrante?.filiacao2 ?? '') };
      } catch { /* ignore */ }
    }
    return { filiacao2: integrante?.filiacao2 ?? '' };
  });
  const [checkboxes, setCheckboxes] = useState<Record<string, boolean>>(() => {
    const filiacaoSalva = respostas['filiacao'];
    if (filiacaoSalva) {
      try {
        const parsed = JSON.parse(filiacaoSalva);
        return { filiacao2: parsed.filiacao2 === 'nao_sei' };
      } catch { /* ignore */ }
    }
    return {};
  });

  const avancar = async () => {
    if (pergunta.tipo === 'inputs') {
      const valores = pergunta.campos?.reduce((acc, campo) => ({
        ...acc,
        [campo.id]: checkboxes[campo.id] ? 'nao_sei' : (campo.id === 'filiacao1' ? (integrante?.filiacao1 ?? '') : (valoresInput[campo.id] || '')),
      }), {});
      dispatch(setRespostaIndicativo({ [pergunta.id]: JSON.stringify(valores) }));
    }
    const opcao = pergunta.opcoes?.find((o) => o.valor === respostaAtual);
    if (opcao?.encerraFluxo && opcao?.proximaEtapa) {
      if (familia?.nuFamiliar && integrante?.cpf && opcao.codigoAcao) {
        setLoadingNaoIncluir(true);
        await dispatch(registrarAcaoIndicativoNascimento({
          numeroFamiliar: familia.nuFamiliar,
          cpfIntegrante: integrante.cpf,
          codigoAcao: opcao.codigoAcao,
        }));
        setLoadingNaoIncluir(false);
      }
      dispatch(setEtapaIndicativo(opcao.proximaEtapa));
      return;
    }
    if (etapaAtual < total - 1) {
      dispatch(setEtapaPerguntaAtual(etapaAtual + 1));
    } else {
      dispatch(setEtapaIndicativo('resumo'));
    }
  };

  const voltar = () => {
    dispatch(setEtapaPerguntaAtual(etapaAtual - 1));
  };

  const fechar = () => setModalAberto(true);

  const handleCancelarNasPerguntas = async () => {
    if (familia?.nuFamiliar && integrante?.cpf) {
      setLoadingNaoIncluir(true);
      await dispatch(registrarAcaoIndicativoNascimento({
        numeroFamiliar: familia.nuFamiliar,
        cpfIntegrante: integrante.cpf,
        codigoAcao: AcaoIndicativoNascimento.NAO_DESEJA_INCLUIR,
      }));
      setLoadingNaoIncluir(false);
    }
    setModalAberto(false);
    dispatch(setEtapaIndicativo('compareca-ao-cras'));
  };

  const selecionar = (valor: string) => {
    dispatch(setRespostaIndicativo({ [pergunta.id]: valor }));
  };

  const podeAvancar = () => {
    if (pergunta.tipo === 'opcoes') return !!respostaAtual;
    return pergunta.campos?.every(
      (campo) => campo.somenteLeitura || checkboxes[campo.id] || !!valoresInput[campo.id]
    ) ?? true;
    
  };

  const renderOpcoes = () => (
    <div className={styles.opcoes}>
      {pergunta.opcoes?.map((opcao) => (
        <label key={opcao.valor} className={styles.opcao} onClick={() => selecionar(opcao.valor)}>
          <div className={`${styles.radio} ${respostaAtual === opcao.valor ? styles.radioSelecionado : ''}`} />
          <span className={styles.opcaoLabel}>{opcao.label}</span>
        </label>
      ))}
    </div>
  );

  const getValorCampo = (campoId: string, somenteLeitura?: boolean) => {
    if (campoId === 'filiacao1') return integrante?.filiacao1 ?? '';
    return valoresInput[campoId] || '';
  };

  const renderInputs = () => (
    <div className={styles.campos}>
      {pergunta.campos?.map((campo) => (
        <div key={campo.id} className={styles.campoWrapper}>
          <label className={styles.campoLabel}>{campo.label}</label>
          <input
            className={`${styles.campoInput} ${campo.somenteLeitura || checkboxes[campo.id] ? styles.campoInputReadonly : ''}`}
            value={getValorCampo(campo.id, campo.somenteLeitura)}
            readOnly={campo.somenteLeitura || !!checkboxes[campo.id]}
            placeholder={campo.placeholder}
            onChange={(e) => setValoresInput({ ...valoresInput, [campo.id]: e.target.value })}
          />
          {campo.avisoSomenteLeitura && (
            <p className={styles.campoAviso}>{campo.avisoSomenteLeitura}</p>
          )}
          {campo.checkboxNaoSei && (
            <label className={styles.checkboxWrapper}>
              <input
                type="checkbox"
                checked={!!checkboxes[campo.id]}
                onChange={(e) => {
                  setCheckboxes({ ...checkboxes, [campo.id]: e.target.checked });
                  if (e.target.checked) setValoresInput({ ...valoresInput, [campo.id]: '' });
                }}
              />
              <span className={styles.checkboxLabel}>{campo.checkboxLabel}</span>
            </label>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.fechar} onClick={fechar}>✕</button>
        <span className={styles.headerTitulo}>Novo integrante na família</span>
      </div>

      <div className={styles.conteudo}>
        <div className={styles.progressoWrapper}>
          <div className={styles.progressoLinha} />
          {Array.from({ length: perguntasVisiveis.length + 1 }).map((_, i) => (
            <React.Fragment key={i}>
              <div
                className={`${styles.progressoPonto} ${i <= etapaAtual ? styles.progressoPontoAtivo : ''}`}
              />
              {i < perguntasVisiveis.length && <div className={styles.progressoEspaco} />}
            </React.Fragment>
          ))}
        </div>

        <p className={styles.titulo}>{pergunta.titulo(nomeCrianca, nomeRF)}</p>
        {pergunta.descricao && <p className={styles.descricao}>{pergunta.descricao}</p>}

        {pergunta.tipo === 'opcoes' ? renderOpcoes() : renderInputs()}
      </div>

      <div className={styles.footer}>
        {etapaAtual > 0 && (
          <button className={styles.botaoAnterior} onClick={voltar}>
            Anterior
          </button>
        )}
        <BrButton
          className={styles.botaoAvancar}
          onClick={avancar}
          disabled={!podeAvancar() || loadingNaoIncluir}
        >
          {loadingNaoIncluir ? <div className="loading small"></div> : (isUltima ? 'Concluir' : 'Avançar')}
        </BrButton>
      </div>
      <ModalNaoIncluir
        open={modalAberto}
        variante="perguntas"
        nomeIntegrante={nomeCrianca}
        onContinuar={() => setModalAberto(false)}
        onNaoIncluir={handleCancelarNasPerguntas}
        loadingNaoIncluir={loadingNaoIncluir}
      />
    </div>
  );
};

export default IndicativoNascimentoPerguntas;
